/**
 * Helper utilities for DOM observer bridge
 */

import type { ContextMetadata } from '../../context/types';

/**
 * Context ID constants for DOM observer contexts.
 * Centralized to ensure consistency across the bridge.
 */
export const DOM_CONTEXT_IDS = {
  TABLE_SELECTION: 'auto-table-selection',
  PAGE_STRUCTURE: 'auto-page-structure',
  VISIBLE_TABLES: 'auto-visible-tables',
  PAGE_ACTIONS: 'auto-page-actions',
  CHARTS: 'auto-charts',
} as const;

/**
 * Context scope constants
 */
export const DOM_CONTEXT_SCOPES = {
  TABLE_SELECTION: 'table-selection',
  PAGE: 'page',
} as const;

/**
 * Stable metadata objects for context registration.
 * These are module-level constants to prevent unnecessary re-renders.
 */
export const TABLE_SELECTION_METADATA: Partial<ContextMetadata> = {
  type: 'other',
  priority: 'high',
  source: 'dom-observer',
} satisfies Partial<ContextMetadata>;

export const PAGE_STRUCTURE_METADATA: Partial<ContextMetadata> = {
  type: 'page-data',
  priority: 'high',
  source: 'dom-observer',
} satisfies Partial<ContextMetadata>;

export const VISIBLE_TABLES_METADATA: Partial<ContextMetadata> = {
  type: 'page-data',
  priority: 'medium',
  source: 'dom-observer',
} satisfies Partial<ContextMetadata>;

export const PAGE_ACTIONS_METADATA: Partial<ContextMetadata> = {
  type: 'page-data',
  priority: 'medium',
  source: 'dom-observer',
} satisfies Partial<ContextMetadata>;

export const CHARTS_METADATA: Partial<ContextMetadata> = {
  type: 'page-data',
  priority: 'high',
  source: 'dom-observer',
} satisfies Partial<ContextMetadata>;

/**
 * DOM Utility Functions
 */

/**
 * Check if an HTML element is visible in the viewport.
 *
 * @param element - The element to check
 * @returns True if element is visible
 */
export function isElementVisible(element: HTMLElement): boolean {
  return element.offsetParent !== null;
}

/**
 * Check if a button or link is disabled.
 *
 * @param button - The button or link element
 * @returns True if element is disabled
 */
export function isButtonDisabled(button: HTMLElement): boolean {
  if (button instanceof HTMLButtonElement) {
    return button.disabled;
  }
  return button.getAttribute('aria-disabled') === 'true';
}

/**
 * Check if an element is within the main content area
 * (not in nav, footer, or banner).
 *
 * @param element - The element to check
 * @returns True if element is in main content
 */
export function isInMainContent(element: HTMLElement): boolean {
  return !element.closest('nav, footer, [role="banner"]');
}

/**
 * Text Utility Functions
 */

/**
 * Clean a table name by removing special characters and normalizing.
 *
 * @param name - Raw table name
 * @returns Cleaned table name
 */
export function cleanTableName(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .toLowerCase();
}

/**
 * Truncate text to a maximum length.
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength);
}

/**
 * Check if text length is within acceptable bounds.
 *
 * @param text - Text to check
 * @param minLength - Minimum acceptable length
 * @param maxLength - Maximum acceptable length
 * @returns True if text is within bounds
 */
export function isTextLengthValid(text: string, minLength: number, maxLength: number): boolean {
  return text.length >= minLength && text.length <= maxLength;
}

/**
 * ID Generation Functions
 */

/**
 * Generate a unique table context ID.
 *
 * @param pathname - Current page path
 * @param tableName - Name of the table
 * @returns Unique table ID
 */
export function generateTableId(pathname: string, tableName: string): string {
  return `auto-table-${cleanTableName(tableName)}`;
}

/**
 * Debouncing and Throttling Constants
 */

/**
 * Debounce delay for DOM scanning (milliseconds).
 * Waits for this much "silence" before executing scans.
 */
export const SCAN_DEBOUNCE_DELAY = 1000; // 1 second

/**
 * Throttle delay for mutation observer (milliseconds).
 * Minimum time between mutation-triggered scans.
 */
export const MUTATION_THROTTLE_DELAY = 2000; // 2 seconds

/**
 * Throttle delay for user interactions (milliseconds).
 * Minimum time between interaction-triggered scans.
 */
export const INTERACTION_THROTTLE_DELAY = 1000; // 1 second

/**
 * Data Limit Constants
 */

/**
 * Maximum number of headings to capture from a page.
 */
export const MAX_HEADINGS = 10;

/**
 * Maximum number of metrics to capture from a page.
 */
export const MAX_METRICS = 15;

/**
 * Maximum number of actions to capture from a page.
 */
export const MAX_ACTIONS = 20;

/**
 * Maximum text length for button/action labels.
 */
export const MAX_ACTION_LABEL_LENGTH = 50;

/**
 * Maximum text length for headings.
 */
export const MAX_HEADING_TEXT_LENGTH = 100;

/**
 * Maximum text length for metric labels.
 */
export const MAX_METRIC_LABEL_LENGTH = 50;

/**
 * Maximum text length for metric values.
 */
export const MAX_METRIC_TEXT_LENGTH = 200;
